import { supabase } from '../supabaseClient';
import {
  hasNativePushSubscription,
  saveNativePushSubscription,
  subscribeNativePush,
  syncNativePushSubscription,
} from './nativePush';
import {
  ensureOneSignalLogin,
  getOneSignalSdk,
  hasActivePushSubscription,
  initializeOneSignal,
  isOneSignalReady,
  resetOneSignalInit,
  waitForOneSignalReady,
  waitForPushSubscription,
} from './oneSignalInit';

const STORAGE_KEY = 'push_notifications_enabled';

export const isPushSupported = () =>
  typeof window !== 'undefined' &&
  'Notification' in window &&
  'serviceWorker' in navigator;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const shouldPreferNativePush = () => {
  const userAgent = navigator.userAgent || '';
  return /SamsungBrowser|Edg\//i.test(userAgent);
};

const readOptedIn = () => {
  if (!isOneSignalReady()) {
    return false;
  }

  try {
    const oneSignal = getOneSignalSdk();
    if (oneSignal?.User?.PushSubscription?.optedIn !== undefined) {
      return Boolean(oneSignal.User.PushSubscription.optedIn);
    }
    return hasActivePushSubscription();
  } catch {
    return false;
  }
};

export const isPushMarkedEnabled = () => {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(STORAGE_KEY) === 'true';
};

export const markPushEnabled = () => {
  localStorage.setItem(STORAGE_KEY, 'true');
};

export const clearPushEnabled = () => {
  localStorage.removeItem(STORAGE_KEY);
};

export const getPushNotificationStatus = async ({ syncNative = false } = {}) => {
  if (!isPushSupported()) {
    return {
      supported: false,
      enabled: false,
      permission: 'unsupported',
      optedIn: false,
    };
  }

  const permission = window.Notification.permission;

  const oneSignalOptedIn = readOptedIn();
  const oneSignalSubscribed = hasActivePushSubscription();
  let nativeSubscribed = await hasNativePushSubscription();

  if (nativeSubscribed && syncNative) {
    try {
      nativeSubscribed = await syncNativePushSubscription();
    } catch (error) {
      console.warn('No se pudo sincronizar la suscripción push nativa:', error);
      nativeSubscribed = false;
    }
  }
  const optedIn = oneSignalOptedIn || nativeSubscribed;
  const subscribed = oneSignalSubscribed || nativeSubscribed;
  const enabled = permission === 'granted' && subscribed;

  if (enabled) {
    markPushEnabled();
  } else if (isPushMarkedEnabled()) {
    clearPushEnabled();
  }

  return {
    supported: true,
    enabled,
    permission,
    optedIn,
    subscribed,
  };
};

const requestBrowserPermission = async () => {
  if (window.Notification.permission === 'granted') {
    return true;
  }

  if (window.Notification.permission === 'denied') {
    const error = new Error('PERMISSION_DENIED');
    error.code = 'PERMISSION_DENIED';
    throw error;
  }

  const result = await window.Notification.requestPermission();
  if (result !== 'granted') {
    const error = new Error('PERMISSION_DENIED');
    error.code = 'PERMISSION_DENIED';
    throw error;
  }

  return true;
};

const ensureOneSignalSdkReady = async () => {
  initializeOneSignal();
  let sdkReady = await waitForOneSignalReady();

  if (!sdkReady) {
    resetOneSignalInit();
    await wait(400);
    initializeOneSignal();
    sdkReady = await waitForOneSignalReady();
  }

  if (!sdkReady || !isOneSignalReady()) {
    throw new Error(
      'No se pudo conectar con el servicio de notificaciones. Recarga la página e intenta de nuevo.'
    );
  }

  return true;
};

const registerOneSignalSubscription = async () => {
  const oneSignal = getOneSignalSdk();

  if (!oneSignal?.User?.PushSubscription) {
    throw new Error('OneSignal no cargó el módulo de suscripción push.');
  }

  if (oneSignal.Notifications?.requestPermission) {
    const granted = await oneSignal.Notifications.requestPermission();
    if (!granted) {
      const error = new Error('PERMISSION_DENIED');
      error.code = 'PERMISSION_DENIED';
      throw error;
    }
  }

  try {
    await oneSignal.User.PushSubscription.optIn();
  } catch (error) {
    console.warn('optIn OneSignal:', error);
  }

  const subscription = await waitForPushSubscription();

  if (!subscription?.id && !subscription?.token) {
    throw new Error(
      'Tu navegador aceptó el permiso, pero no se registró la suscripción push. Cierra todas las pestañas del sitio, ábrelo de nuevo y pulsa el botón otra vez.'
    );
  }

  return subscription;
};

const registerNativePushSubscription = async () => {
  const subscription = await subscribeNativePush();
  await saveNativePushSubscription(subscription);
  return subscription;
};

const showLocalWelcomeNotification = () => {
  if (window.Notification?.permission !== 'granted') return;

  try {
    new Notification('¡Notificaciones activadas!', {
      body: 'Te avisaremos cuando el estado de tus remesas cambie.',
      icon: '/notification-icon.png',
      badge: '/notification-badge-v2.png',
    });
  } catch (error) {
    console.warn('No se pudo mostrar notificación local:', error);
  }
};

const sendWelcomePushNotification = async (userId, subscriptionId, channel = 'auto') => {
  const { data, error } = await supabase.functions.invoke('smooth-function', {
    body: {
      type: 'welcome',
      user_id: userId,
      subscription_id: subscriptionId,
      channel,
    },
  });

  if (error) {
    throw error;
  }

  return data;
};

export const enablePushNotifications = async (userId) => {
  if (!isPushSupported()) {
    throw new Error('Tu navegador no admite notificaciones push.');
  }

  if (!userId) {
    throw new Error('Debes iniciar sesión para activar las notificaciones.');
  }

  await requestBrowserPermission();

  let subscription = null;
  let channel = 'onesignal';

  if (shouldPreferNativePush()) {
    subscription = await registerNativePushSubscription();
    channel = 'native';
  } else {
    try {
      await ensureOneSignalSdkReady();
      subscription = await registerOneSignalSubscription();
      await ensureOneSignalLogin(userId, { throwOnFailure: true });
    } catch (error) {
      console.warn('OneSignal bloqueado o no disponible, usando Web Push nativo:', error);
      subscription = await registerNativePushSubscription();
      channel = 'native';
    }
  }

  await wait(1200);

  try {
    await sendWelcomePushNotification(userId, subscription?.id, channel);
  } catch (error) {
    console.warn('Push de bienvenida vía servidor:', error);
  }

  showLocalWelcomeNotification();
  markPushEnabled();

  return getPushNotificationStatus({ syncNative: true });
};
